import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Label,
  Form,
  Button,
} from "reactstrap";
import { toast } from "react-toastify";

interface EditSegmentProps {
  isOpen: boolean;
  toggle: () => void;
  loading: boolean;
  segmentFormData: {
    name: string;
    description: string;
    updateCycle: string;
    isLiveSegmentation: boolean;
    isActive: boolean;
  };
  setSegmentFormData: React.Dispatch<
    React.SetStateAction<{
      name: string;
      description: string;
      updateCycle: string;
      isLiveSegmentation: boolean;
      isActive: boolean;
    }>
  >;
  recordsFiltered: number | null;
  selectedPresetId: string | null;
  onUpdate: () => void;
}

interface FormErrors {
  name?: string;
  description?: string;
  updateCycle?: string;
}

const EditSegment: React.FC<EditSegmentProps> = ({
  isOpen,
  toggle,
  loading,
  segmentFormData,
  setSegmentFormData,
  recordsFiltered,
  selectedPresetId,
  onUpdate,
}) => {
  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!segmentFormData.name.trim()) {
      newErrors.name = "Segment name is required";
    }

    if (!segmentFormData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!segmentFormData.updateCycle) {
      newErrors.updateCycle = "Update cycle is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = () => {
    if (validateForm()) {
      onUpdate();
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setSegmentFormData({
      ...segmentFormData,
      [field]: value,
    });
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const isFormValid = (): boolean => {
    return (
      segmentFormData.name.trim() !== "" &&
      segmentFormData.description.trim() !== "" &&
      segmentFormData.updateCycle !== ""
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Edit Segment</ModalHeader>
      <ModalBody>
        <Form>
          <div className="mb-3">
            <Label className="form-label">
              Segment Name <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Enter segment name"
              value={segmentFormData.name}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              invalid={!!errors.name}
            />
            {errors.name && (
              <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                {errors.name}
              </div>
            )}
          </div>

          <div className="mb-3">
            <Label className="form-label">
              Description <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows="3"
              placeholder="Enter description"
              value={segmentFormData.description}
              onChange={(e) => handleFieldChange("description", e.target.value)}
              invalid={!!errors.description}
            />
            {errors.description && (
              <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                {errors.description}
              </div>
            )}
          </div>

          <div className="mb-3">
            <Label className="form-label">
              Update Cycle <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={segmentFormData.updateCycle}
              onChange={(e) => handleFieldChange("updateCycle", e.target.value)}
              invalid={!!errors.updateCycle}
            >
              <option value="">Select update cycle</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              {/* <option value="monthly">Monthly</option> */}
            </Input>
            {errors.updateCycle && (
              <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                {errors.updateCycle}
              </div>
            )}
          </div>

          <div className="mb-3">
            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="isLiveSegmentation"
                checked={segmentFormData.isLiveSegmentation}
                onChange={(e) =>
                  setSegmentFormData({
                    ...segmentFormData,
                    isLiveSegmentation: e.target.checked,
                  })
                }
              />
              <Label className="form-check-label" htmlFor="isLiveSegmentation">
                Live Segmentation
              </Label>
            </div>
          </div>

          <div className="mb-3">
            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="isActive"
                checked={segmentFormData.isActive}
                onChange={(e) =>
                  setSegmentFormData({
                    ...segmentFormData,
                    isActive: e.target.checked,
                  })
                }
              />
              <Label className="form-check-label" htmlFor="isActive">
                Active
              </Label>
            </div>
          </div>

          {recordsFiltered !== null && (
            <div className="mb-3">
              <Label className="form-label text-muted">
                Total Users: <strong>{recordsFiltered}</strong>
              </Label>
            </div>
          )}
        </Form>
      </ModalBody>
      <div className="modal-footer">
        <Button
          className="btn btn-dark"
          size="sm"
          onClick={toggle}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          className="btn btn-dark"
          size="sm"
          onClick={handleUpdate}
          disabled={loading || !isFormValid()}
        >
          {loading && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
          )}
          Update
        </Button>
      </div>
    </Modal>
  );
};

export default EditSegment;